import { Stack, Typography, useModal } from '@ui-components/neo.tax';
import { ReactComponent as ArrowDown } from '@ui-components/neo.tax/dist/icons/arrow-down-1.svg';
import React, { useState } from 'react';

import { useCurrentUser } from '@neo.tax/shared/hooks';
import {
  OrganizationMembersTableItem,
  OrganizationMembersTableItemInvitation,
  OrganizationMemberTableItemMember,
} from '@neo.tax/shared/types';
import { companyMemberItemScopeDisplayName, isInvitation } from '@neo.tax/shared/utils';

import { DeleteInvitedMemberModal } from '../delete-invited-member-modal';
import { DeleteMemberModal } from '../delete-member-modal';
import { EditOrganizationMemberModal } from '../edit-organization-member-modal';
import { ResendInvitationModal } from '../resend-invitation-modal';
import { BottomStack, DeleteButton, ResendButton, StyledButton } from './company-member-drawer.styled';

export const MemberInformation = ({
  close,
  member,
  organizationId,
}: {
  organizationId: string;
  member: OrganizationMembersTableItem;
  close: () => void;
}): React.ReactElement => {
  const { user } = useCurrentUser();
  const [memberToDelete, setMemberToDelete] = useState<OrganizationMemberTableItemMember>();
  const [organizationInvitationToDelete, setOrganizationInvitationToDelete] =
    useState<OrganizationMembersTableItemInvitation>();
  const [organizationInvitationToResend, setOrganizationInvitationToResend] =
    useState<OrganizationMembersTableItemInvitation>();
  const { isOpen: isEditMemberModalOpen, setIsOpen: setIsEditMemberModalOpen } = useModal();

  const onCancelToggleModal = (): void => {
    setMemberToDelete(undefined);
    setOrganizationInvitationToDelete(undefined);
    setOrganizationInvitationToResend(undefined);
  };

  const onClickDelete = (): void => {
    if (isInvitation(member)) {
      setOrganizationInvitationToDelete(member);
    } else {
      setMemberToDelete(member);
    }
  };

  const onClickResend = (): void => {
    if (isInvitation(member)) {
      setOrganizationInvitationToResend(member);
    }
  };

  const isExistingUser = !isInvitation(member);
  const disableScopeUpdate = member.id === user?.id || !isExistingUser;

  return (
    <>
      <Stack direction="column" paddingY={4} fullWidth fullHeight justifyContent="space-between">
        <Stack direction="column" paddingX={6} gap={2} fullWidth>
          <Typography fontSize="sm" fontWeight="medium">
            Role
          </Typography>
          <StyledButton
            size="sm"
            variant="white"
            rounded="regular"
            disabled={disableScopeUpdate}
            onClick={(): void => setIsEditMemberModalOpen(true)}
          >
            <Typography fontSize="sm" fontWeight="medium">
              {companyMemberItemScopeDisplayName[member.scope]}
              <Typography fontSize="inherit"> View/Edit Specific Companies</Typography>
            </Typography>
            <ArrowDown width={10} />
          </StyledButton>
        </Stack>
        {member.id !== user?.id && (
          <BottomStack justifyContent="end" gap={3} paddingY={5} paddingX={6} boxShadow="sm">
            {isInvitation(member) && member.derivedState !== 'ACCEPTED' && member.derivedState !== 'REVOKED' && (
              <ResendButton size="sm" variant="white" rounded="regular" onClick={onClickResend}>
                Resend invitation
              </ResendButton>
            )}
            <DeleteButton size="sm" variant="action" rounded="regular" onClick={onClickDelete}>
              Delete
            </DeleteButton>
          </BottomStack>
        )}
      </Stack>
      <DeleteMemberModal
        member={memberToDelete}
        organizationId={organizationId}
        onDelete={close}
        isOpen={!!memberToDelete}
        setIsOpen={onCancelToggleModal}
      />
      <DeleteInvitedMemberModal
        invitation={organizationInvitationToDelete}
        onDelete={close}
        isOpen={!!organizationInvitationToDelete}
        setIsOpen={onCancelToggleModal}
      />
      <ResendInvitationModal
        invitation={organizationInvitationToResend}
        onResend={close}
        isOpen={!!organizationInvitationToResend}
        setIsOpen={onCancelToggleModal}
      />
      {isExistingUser && (
        <EditOrganizationMemberModal
          member={member}
          organizationId={organizationId}
          isOpen={isEditMemberModalOpen}
          setIsOpen={setIsEditMemberModalOpen}
        />
      )}
    </>
  );
};
